import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ArrowDown,
  ClipboardList,
  Brain,
  Boxes,
  UserCheck,
  Network,
  FolderGit2,
  GitBranch,
  FileText,
  CheckCircle2,
  BookOpen,
  Building2,
  Briefcase,
  Workflow,
  Lock,
  ScrollText,
  ShieldCheck,
  Mail,
} from 'lucide-react';
import SEO from '../components/SEO';
import { CloverMark, CollabityWordmark } from '../components/CloverMark';
import { G, Reveal, SectionLabel, Heading, ScrollProgress } from '../components/collabity/Reveal';
import { ArchitectureFlow } from '../components/collabity/ArchitectureFlow';
import { FlowTimeline } from '../components/collabity/FlowTimeline';
import { RoadmapTabs } from '../components/collabity/RoadmapTabs';

const mvpCapabilities = [
  { title: 'Structured Project Intake', desc: 'Capture business requirements, objectives, constraints and open questions.', icon: ClipboardList },
  { title: 'AI-Assisted Scoping', desc: 'Convert incomplete requirements into structured project information while keeping human confirmation in control.', icon: Brain },
  { title: 'Capability Decomposition', desc: 'Translate project requirements into the skills and capabilities required for delivery.', icon: Boxes },
  { title: 'Professional Profiles & Vetting', desc: 'Organise professional capability information and project suitability.', icon: UserCheck },
  { title: 'Team Formation', desc: 'Structure multidisciplinary teams according to the capabilities the project actually requires.', icon: Network },
  { title: 'Project & Milestone Structure', desc: 'Create work packages, responsibilities, milestones and acceptance criteria.', icon: FolderGit2 },
  { title: 'Delivery Governance', desc: 'Support project records, decisions, changes, dependencies and escalation.', icon: GitBranch },
  { title: 'Project Evidence', desc: 'Keep relevant evidence attached to milestones and delivery decisions.', icon: FileText },
  { title: 'Acceptance Records', desc: 'Record whether agreed work is accepted, requires revision or creates an exception.', icon: CheckCircle2 },
  { title: 'Project Learning', desc: 'Create the foundation for reusable project patterns and future decision support.', icon: BookOpen },
];

const problemQuestions = [
  'What exactly are we trying to achieve?',
  'What should the project include?',
  'Which capabilities are actually required?',
  'Who should be responsible for each part?',
  'How should milestones be accepted?',
  'What happens when requirements change?',
  'How do we retain what we learned for the next project?',
];

const disconnectedBoxes = ['Brief', 'Tools', 'Freelancers', 'Messages', 'Project Board', 'Payments', 'Files'];
const businessSituations = ['Digital-product delivery', 'Workflow redesign', 'Automation implementation', 'Go-to-market projects', 'Multidisciplinary campaigns', 'Research and implementation projects', 'Projects requiring several different capabilities'];
const businessStrongest = ['The requirement is not fully defined', 'More than one capability is needed', 'The internal team is stretched', 'Coordination risk is material', 'Accountability and acceptance matter'];
const professionalValue = ['Clearer briefs', 'Defined responsibilities', 'Visibility of dependencies', 'Structured milestones', 'Agreed acceptance criteria', 'Clearer change management', 'Stronger evidence of contribution', 'Multidisciplinary teams'];
const professionalCapability = ['Skills', 'Portfolios', 'Relevant experience', 'Availability', 'Working preferences', 'Project-specific suitability', 'Contribution history over time'];
const automateSteps = ['Workflow diagnosis', 'Readiness assessment', 'Process redesign', 'Automation blueprint', 'Controlled implementation'];
const fragmentedFlow = ['Problem', 'Separate consultant', 'Separate freelancer search', 'Separate PM tool', 'Messages', 'Delivery', 'Knowledge lost'];
const collabityFlow = ['Problem', 'Structured project', 'Capability', 'Team', 'Governed delivery', 'Acceptance', 'Learning'];
const defensibility = ['Company-controlled project-delivery methods', 'Reusable project structures', 'Capability intelligence', 'Project-specific contribution history', 'Customer project memory', 'Delivery patterns', 'Exception handling', 'Acceptance records', 'Evaluation assets', 'Trusted professional participation'];
const trustPrinciples = ['Human confirmation of scope', 'Clear decision rights', 'Milestone acceptance', 'Traceable changes', 'Professional suitability review', 'Data permissions', 'Appropriate security controls', 'Specialist review where required', 'No autonomous high-impact decisions'];
const statusRows = [
  ['Product', 'Collabity'],
  ['Company', 'OWLWORKLABS LTD'],
  ['Stage', 'Closed-Beta MVP'],
  ['Current Focus', 'Product hardening, controlled validation and project-delivery architecture'],
  ['Commercial Approach', 'Founder-led validation progressing toward controlled UK commercialisation'],
  ['Primary Product Direction', 'Structured multidisciplinary project delivery'],
  ['AI Position', 'Human-supervised and assistive'],
  ['Broader Roadmap', 'Project intelligence, reusable learning, professional collaboration and scalable delivery infrastructure'],
];

const CollabityPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-neutral-200 antialiased selection:bg-[#34c800] selection:text-black">
      <SEO
        title="Collabity | Project-Delivery Architecture by OwlWorkLabs"
        description="Collabity is a developing project-delivery architecture that helps businesses turn under-defined requirements into structured projects, assemble the right capabilities, govern delivery and retain useful learning. Closed-beta MVP under development."
        canonical="https://www.owlworklabs.com/collabity"
      />
      <ScrollProgress />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 pt-3 sm:pt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between rounded-2xl bg-black/70 backdrop-blur-xl border border-white/10">
          <Link to="/" className="flex items-center gap-2.5" data-testid="collabity-logo-link">
            <CloverMark size={34} leafA={G} leafB="#ffffff" />
            <CollabityWordmark className="text-xl" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:flex items-center gap-2 text-sm font-medium text-neutral-400 hover:text-white transition-colors" data-testid="collabity-back-link">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
            <a
              href="mailto:owlworklabs@gmail.com?subject=Following Collabity's Development"
              className="flex items-center gap-2 px-4 py-2 font-display font-bold text-black rounded-full transition-transform hover:scale-[1.03] text-sm"
              style={{ background: G }}
              data-testid="collabity-header-cta"
            >
              Follow Development
            </a>
          </div>
        </div>
      </header>

      {/* 1. Hero */}
      <section className="relative overflow-hidden pt-36 sm:pt-44 pb-20 sm:pb-28">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-10 -right-16 opacity-[0.06]">
            <CloverMark size={460} leafA={G} leafB="#ffffff" className="animate-clover-spin" />
          </div>
          <div className="absolute top-40 left-4 opacity-[0.10]">
            <CloverMark size={70} leafA={G} leafB="#3f3f46" spin />
          </div>
          <div className="absolute bottom-16 right-1/4 opacity-[0.08]">
            <CloverMark size={44} leafA={G} leafB="#3f3f46" spin />
          </div>
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                'linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
              maskImage: 'radial-gradient(ellipse 65% 55% at 40% 0%, #000 30%, transparent 100%)',
              WebkitMaskImage: 'radial-gradient(ellipse 65% 55% at 40% 0%, #000 30%, transparent 100%)',
            }}
          />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="cb-reveal cb-in inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border" style={{ borderColor: `${G}44`, background: `${G}12` }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: G }} />
            <span className="text-xs sm:text-sm font-medium tracking-wide" style={{ color: G }}>Closed-Beta MVP • Under Development</span>
          </div>

          <h1 className="font-display font-extrabold text-white tracking-tight leading-[0.98] mt-7 text-[2.6rem] sm:text-6xl lg:text-7xl max-w-4xl">
            <span className="block cb-reveal cb-in" style={{ animationDelay: '80ms' }}>From business requirement</span>
            <span className="block cb-reveal cb-in" style={{ animationDelay: '220ms' }}>
              to <span style={{ color: G }}>accountable delivery.</span>
            </span>
          </h1>

          <p className="cb-reveal cb-in text-base sm:text-lg text-neutral-400 max-w-2xl mt-6 leading-relaxed" style={{ animationDelay: '360ms' }}>
            Collabity is a developing project-delivery architecture designed to help businesses turn
            under-defined requirements into structured projects, assemble the right capabilities, govern
            delivery and retain useful learning from completed work.
          </p>

          <div className="cb-reveal cb-in flex flex-col sm:flex-row gap-3 mt-9" style={{ animationDelay: '500ms' }}>
            <a href="#architecture" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 font-display font-bold text-black rounded-full transition-transform hover:scale-[1.03]" style={{ background: G }} data-testid="hero-explore-cta">
              Explore the Architecture <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#business" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 font-medium text-white rounded-full border border-white/15 hover:border-white/40 transition-colors" data-testid="hero-business-cta">
              For Businesses
            </a>
            <a href="#professionals" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 font-medium text-white rounded-full border border-white/15 hover:border-white/40 transition-colors" data-testid="hero-professionals-cta">
              For Professionals
            </a>
          </div>
        </div>
      </section>

      {/* 2. Problem */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>The Problem</SectionLabel>
            <Heading className="max-w-2xl">The tools exist. The delivery gap remains.</Heading>
            <p className="text-neutral-400 max-w-2xl mt-5 leading-relaxed">
              Businesses already have access to freelancers, agencies, AI tools, project-management software,
              automation platforms and consultants. Those tools do not necessarily answer the questions that
              decide whether a project actually lands.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="grid md:grid-cols-2 gap-3 mt-10 max-w-3xl">
              {problemQuestions.map((q) => (
                <div key={q} className="flex items-start gap-3 px-4 py-3.5 rounded-xl border border-white/8 bg-white/[0.02] hover:border-[#34c800]/40 transition-colors">
                  <span className="mt-0.5 text-sm font-mono" style={{ color: G }}>?</span>
                  <span className="text-sm text-neutral-300 leading-snug">{q}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={180}>
            <div className="mt-14 rounded-2xl border border-white/8 bg-white/[0.02] p-6 sm:p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-6">Today: fragmented</p>
              <div className="flex flex-wrap gap-3">
                {disconnectedBoxes.map((b) => (
                  <span key={b} className="px-4 py-2.5 rounded-lg border border-dashed border-white/15 bg-black/40 text-sm text-neutral-400">{b}</span>
                ))}
              </div>
              <div className="flex items-center justify-center my-7 text-neutral-600">
                <ArrowDown className="w-6 h-6" />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] mb-4" style={{ color: G }}>With Collabity: one governed journey</p>
              <div className="rounded-xl px-5 py-4 flex items-center gap-3" style={{ border: `1px solid ${G}44`, background: `${G}0d` }}>
                <Workflow className="w-5 h-5 flex-shrink-0" style={{ color: G }} />
                <span className="text-sm text-neutral-200">Collabity connects the fragments into a single, governed project journey — from requirement to accepted outcome and retained learning.</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3. Architecture */}
      <section id="architecture" className="py-20 sm:py-28 bg-white/[0.015] border-y border-white/5 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>What Collabity Is</SectionLabel>
            <Heading className="max-w-2xl">A connected operating architecture for project delivery</Heading>
            <p className="text-neutral-400 max-w-2xl mt-5 leading-relaxed">
              Not only a freelance marketplace, a project-management board, an AI assistant, a talent directory
              or an automation agency — Collabity connects several parts of project delivery into one
              structured operating method. Hover or tap a stage.
            </p>
          </Reveal>
          <Reveal delay={140} className="mt-14">
            <ArchitectureFlow />
          </Reveal>
        </div>
      </section>

      {/* 4. Project Flow */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>How A Project Could Flow</SectionLabel>
            <Heading>One path, with accountable humans in control</Heading>
            <p className="text-neutral-400 mt-5 leading-relaxed">
              A single project journey through Collabity. AI is assistive throughout — accountable humans remain
              responsible for scope, selection, acceptance and decisions.
            </p>
          </Reveal>
          <div className="mt-12">
            <FlowTimeline />
          </div>
        </div>
      </section>

      {/* 5. MVP */}
      <section className="py-20 sm:py-28 bg-white/[0.015] border-y border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>Current MVP</SectionLabel>
            <Heading>Where Collabity is today</Heading>
            <p className="text-neutral-400 max-w-2xl mt-5 leading-relaxed">
              Collabity currently exists as a functional closed-beta MVP and demonstration environment. The
              current objective is controlled testing, product hardening and refinement before wider commercial
              use. Not every module below is production-ready.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {mvpCapabilities.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="group h-full rounded-2xl border border-white/8 bg-[#111] p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#34c800]/50">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${G}14` }}>
                    <Icon className="w-5 h-5" style={{ color: G }} />
                  </div>
                  <h3 className="font-display font-bold text-white text-base mb-2 leading-snug">{c.title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6. Professional Participation */}
      <section id="professionals" className="py-20 sm:py-28 scroll-mt-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>Professional Participation</SectionLabel>
            <Heading className="max-w-2xl">Built around capability, not just job titles</Heading>
            <p className="text-neutral-400 max-w-2xl mt-5 leading-relaxed">
              Collabity is being designed to understand what a project actually requires and then identify
              suitable capability. The aim is not simply a large freelancer directory — the long-term goal is
              better project-specific team formation.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mt-10">
              {professionalCapability.map((item) => (
                <div key={item} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/8 bg-white/[0.02]">
                  <UserCheck className="w-4 h-4 flex-shrink-0" style={{ color: G }} />
                  <span className="text-sm text-neutral-300">{item}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 7. For Businesses */}
      <section id="business" className="py-20 sm:py-28 bg-white/[0.015] border-y border-white/5 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>For Businesses</SectionLabel>
            <Heading className="max-w-3xl">For businesses that need outcomes, not another tool</Heading>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <Reveal>
              <div className="flex items-center gap-2 mb-5">
                <Building2 className="w-5 h-5" style={{ color: G }} />
                <h3 className="font-display font-bold text-white text-lg">Suitable situations</h3>
              </div>
              <ul className="space-y-3">
                {businessSituations.map((x) => (
                  <li key={x} className="flex items-start gap-3 text-sm text-neutral-300">
                    <ArrowRight className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: G }} /> {x}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={120}>
              <div className="rounded-2xl p-6 sm:p-8 h-full" style={{ border: `1px solid ${G}33`, background: `${G}0a` }}>
                <h3 className="font-display font-bold text-white text-lg mb-5">Strongest use case</h3>
                <ul className="space-y-3">
                  {businessStrongest.map((x) => (
                    <li key={x} className="flex items-start gap-3 text-sm text-neutral-200">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: G }} /> {x}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 8. For Professionals */}
      <section className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>For Professionals</SectionLabel>
            <Heading className="max-w-3xl">Clearer projects. Clearer expectations. Better collaboration.</Heading>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {professionalValue.map((x) => (
              <div key={x} className="group h-full rounded-xl border border-white/8 bg-white/[0.02] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[#34c800]/40">
                <Briefcase className="w-5 h-5 mb-3" style={{ color: G }} />
                <p className="text-sm text-neutral-200 leading-snug">{x}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Collabity + Automate */}
      <section className="py-20 sm:py-28 bg-white/[0.015] border-y border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>Collabity + Automate</SectionLabel>
            <Heading>Automate is one route into the Collabity architecture</Heading>
            <p className="text-neutral-400 mt-5 leading-relaxed">
              OwlWorkLabs is also developing Automate, a diagnostic-to-implementation service for businesses
              dealing with inefficient workflows, fragmented tools or uncertainty around automation and AI.
              Automate starts with the business problem rather than a preferred technology.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="flex flex-wrap items-center gap-2 mt-8">
              {automateSteps.map((x) => (
                <span key={x} className="px-3.5 py-2 rounded-lg border border-white/10 bg-black/40 text-sm text-neutral-300">{x}</span>
              ))}
            </div>
            <p className="text-neutral-400 mt-8 leading-relaxed">
              Suitable implementation work can then use the Collabity delivery architecture for project
              structure, specialist coordination, governance and learning.
            </p>
            <Link to="/automate" className="inline-flex items-center gap-2 mt-6 text-sm font-display font-bold hover:gap-3 transition-all" style={{ color: G }} data-testid="collabity-automate-link">
              Explore Auto-Mate <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 10. Difference */}
      <section className="py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>The Difference</SectionLabel>
            <Heading className="max-w-3xl">The value is in the connection between the stages</Heading>
            <p className="text-neutral-400 max-w-3xl mt-5 leading-relaxed">
              AI, freelance marketplaces, project-management software, professional vetting and milestone
              structures already exist. Collabity's development focus is on connecting the original business
              requirement to capability decisions, project structure, delivery evidence, acceptance and
              reusable learning.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5 mt-12">
            <Reveal>
              <div className="h-full rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                <h3 className="text-sm font-display font-bold text-neutral-500 uppercase tracking-wide mb-5">Traditional fragmented approach</h3>
                <div className="flex flex-wrap items-center gap-2 text-sm text-neutral-400">
                  {fragmentedFlow.map((x) => (
                    <span key={x} className={`px-2.5 py-1.5 rounded-md border border-dashed border-white/15 bg-black/40 ${x === 'Knowledge lost' ? 'text-neutral-600 line-through' : ''}`}>{x}</span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="h-full rounded-2xl p-6" style={{ border: `1px solid ${G}44`, background: `${G}0a` }}>
                <h3 className="text-sm font-display font-bold uppercase tracking-wide mb-5" style={{ color: G }}>Collabity approach</h3>
                <div className="flex flex-wrap items-center gap-2 text-sm text-neutral-200">
                  {collabityFlow.map((x) => (
                    <span key={x} className="px-2.5 py-1.5 rounded-md bg-black/40" style={{ border: `1px solid ${G}44` }}>{x}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 11. Defensibility */}
      <section className="py-20 sm:py-28 bg-white/[0.015] border-y border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>Developing Defensibility</SectionLabel>
            <Heading className="max-w-3xl">A system intended to become stronger through use</Heading>
            <p className="text-neutral-400 max-w-3xl mt-5 leading-relaxed">
              Long-term defensibility is intended to develop gradually through the operating history created
              across real projects.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 mt-10">
              {defensibility.map((x) => (
                <div key={x} className="flex items-start gap-3 py-1.5 border-b border-white/5">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: G }} />
                  <span className="text-sm text-neutral-300 leading-snug">{x}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={180}>
            <div className="mt-10 rounded-2xl border-l-2 bg-white/[0.02] p-6 sm:p-8" style={{ borderColor: G }}>
              <p className="text-neutral-300 leading-relaxed italic">
                “A competitor may be able to reproduce an individual feature. The longer-term objective is to
                build value in the connected operating history and learning created across real projects.”
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 12. Roadmap */}
      <section className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>Roadmap</SectionLabel>
            <Heading className="max-w-3xl">From closed beta to a broader project-delivery system</Heading>
          </Reveal>
          <Reveal delay={120} className="mt-10">
            <RoadmapTabs />
          </Reveal>
          <Reveal delay={160}>
            <div className="mt-8 rounded-2xl border border-white/8 bg-white/[0.02] p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-4 h-4 text-neutral-500" />
                <h3 className="font-display font-bold text-white text-base">Future Funding & Participation Models</h3>
              </div>
              <p className="text-sm text-neutral-400 leading-relaxed">
                Longer-term research may explore compliant ways for communities or supporters to participate in
                selected ventures or projects. Any financial-return, investment, crowdfunding or similar
                functionality would remain outside the current product scope and would only be considered
                following appropriate legal, regulatory and authorised-provider review.
              </p>
              <p className="text-sm text-neutral-500 leading-relaxed mt-4">
                Long-term venture financing pathways, including conventional institutional investment or
                eventual public-market options, remain strategic possibilities rather than current Collabity
                functionality.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 13. Trust */}
      <section className="py-20 sm:py-28 bg-white/[0.015] border-y border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal>
            <SectionLabel>Trust, Governance & Human Control</SectionLabel>
            <Heading className="max-w-3xl">Technology should support accountability, not remove it</Heading>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 mt-10">
              {trustPrinciples.map((x) => (
                <div key={x} className="flex items-start gap-3 py-1.5 border-b border-white/5">
                  <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: G }} />
                  <span className="text-sm text-neutral-300 leading-snug">{x}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-neutral-500 leading-relaxed mt-8 max-w-3xl">
              Any payment functionality involving holding or safeguarding client money would rely on
              appropriately authorised or registered third-party providers and would not imply that OwlWorkLabs
              itself is currently acting as an escrow provider.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 14. Status */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-[#111] p-7 sm:p-10 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 opacity-[0.07]">
                <CloverMark size={200} leafA={G} leafB="#ffffff" />
              </div>
              <div className="flex items-center gap-3 mb-8 relative">
                <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${G}14` }}>
                  <ScrollText className="w-5 h-5" style={{ color: G }} />
                </span>
                <h2 className="font-display font-extrabold text-white text-xl sm:text-2xl">Current Status</h2>
              </div>
              <dl className="divide-y divide-white/8 relative">
                {statusRows.map((row) => (
                  <div key={row[0]} className="py-4 sm:grid sm:grid-cols-3 sm:gap-6">
                    <dt className="text-sm font-display font-bold text-neutral-500">{row[0]}</dt>
                    <dd className="text-sm text-neutral-200 sm:col-span-2 mt-1 sm:mt-0">{row[1]}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 15. Closing */}
      <section className="py-24 sm:py-32 border-t border-white/5 relative overflow-hidden">
        <div className="absolute left-1/2 -translate-x-1/2 top-10 opacity-[0.05] pointer-events-none">
          <CloverMark size={320} leafA={G} leafB="#ffffff" className="animate-clover-spin" />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <Reveal>
            <CloverMark size={48} leafA={G} leafB="#ffffff" spin className="mx-auto mb-7" />
            <h2 className="font-display font-extrabold text-white leading-[1.02] tracking-tight text-3xl sm:text-5xl">
              Better projects start <span style={{ color: G }}>before</span> the project board.
            </h2>
            <p className="text-base sm:text-lg text-neutral-400 mt-6 leading-relaxed">
              Collabity is being built around a simple idea: before businesses need more tools or more people,
              they need a better way to define the work, assemble the right capability and retain accountability
              through delivery.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
              <a href="mailto:owlworklabs@gmail.com?subject=Following Collabity's Development" className="inline-flex items-center justify-center gap-2 px-8 py-4 font-display font-bold text-black rounded-full transition-transform hover:scale-[1.03]" style={{ background: G }} data-testid="closing-follow-cta">
                Follow Collabity's Development <ArrowRight className="w-4 h-4" />
              </a>
              <a href="mailto:owlworklabs@gmail.com?subject=Talk to OwlWorkLabs about Collabity" className="inline-flex items-center justify-center gap-2 px-8 py-4 font-medium text-white rounded-full border border-white/15 hover:border-white/40 transition-colors" data-testid="closing-talk-cta">
                <Mail className="w-4 h-4" /> Talk to OwlWorkLabs
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/8 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <CloverMark size={28} leafA={G} leafB="#ffffff" />
              <CollabityWordmark className="text-base" />
              <span className="text-neutral-600 text-sm ml-1">by OwlWorkLabs</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-neutral-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/automate" className="hover:text-white transition-colors">Auto-Mate</Link>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
              <a href="mailto:owlworklabs@gmail.com" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/8 text-center text-neutral-600 text-sm">
            © {new Date().getFullYear()} OwlWorkLabs Ltd. All rights reserved. · Company No. 16713122 • Collabity is a closed-beta MVP under development.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CollabityPage;
